import { useEffect, useRef, useState } from "react";
import { Copy, CheckCircle2 } from "lucide-react";
import { useLogToolUsage } from "@/lib/clinical-tools";

/**
 * GAD-2 — Anxiety Fast Screen (beyondblue, Kroenke 2007).
 *
 * Two questions, each scored 0–3 on a Likert frequency scale. Total 0–6.
 * A score of ≥3 is the validated cut-point indicating likely generalised
 * anxiety; follow-up GAD-7 or clinical review is recommended.
 *
 * Explicit medico-legal constraint (CLAUDE.md rule 6): we show score +
 * category + "consider GAD-7" as the next-step. We deliberately do NOT
 * surface medication recommendations, treatment plans, or referral-level
 * escalation — those are clinician judgement calls.
 *
 * Reference implementation choices (documented here because the other 11
 * tools will inherit this pattern):
 *  - Radio buttons, not sliders: discrete Likert, each option visible.
 *  - Inline result, no modal: the clinician stays in context.
 *  - Copy-to-clipboard emits a clinician-facing note (score + category +
 *    source citation), not a patient-facing summary. Patient language
 *    risks dictating management and is out of scope for an MVP screener.
 *  - Usage event fires on completion (not on mount) — we're measuring
 *    "tools actually used", not "tools opened then bounced".
 */

interface Question {
  id: "q1" | "q2";
  prompt: string;
}

const QUESTIONS: Question[] = [
  { id: "q1", prompt: "Feeling nervous, anxious or on edge?" },
  { id: "q2", prompt: "Not being able to stop or control worrying?" },
];

const OPTIONS: { value: 0 | 1 | 2 | 3; label: string }[] = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" },
];

type Answer = 0 | 1 | 2 | 3 | null;

export function Gad2Form({ toolSlug }: { toolSlug: string }) {
  const [q1, setQ1] = useState<Answer>(null);
  const [q2, setQ2] = useState<Answer>(null);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const mountedAtRef = useRef<number>(Date.now());

  const { mutate: logUsage } = useLogToolUsage();

  const total = (q1 ?? 0) + (q2 ?? 0);
  const bothAnswered = q1 !== null && q2 !== null;
  const positive = submitted && total >= 3;

  const handleSubmit = () => {
    if (!bothAnswered) return;
    setSubmitted(true);
    const durationSeconds = Math.round((Date.now() - mountedAtRef.current) / 1000);
    logUsage({
      slug: toolSlug,
      payload: { completed: true, durationSeconds },
    });
  };

  const handleReset = () => {
    setQ1(null);
    setQ2(null);
    setSubmitted(false);
    setCopied(false);
    mountedAtRef.current = Date.now();
  };

  const clinicianNote = submitted
    ? `GAD-2 screen: ${total}/6 (${positive ? "positive screen — consider GAD-7 follow-up" : "negative screen"}). Source: beyondblue / Kroenke 2007.`
    : "";

  const handleCopy = async () => {
    if (!clinicianNote) return;
    try {
      await navigator.clipboard.writeText(clinicianNote);
      setCopied(true);
      logUsage({ slug: toolSlug, payload: { outputCopied: true } });
    } catch {
      // Clipboard API unavailable — silent fail (HTTPS-gated on most browsers).
    }
  };

  // Auto-reset the "copied" affordance after 2s.
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <div className="space-y-8">
      {QUESTIONS.map((q, idx) => (
        <fieldset key={q.id} className="border-0 p-0">
          <legend className="mb-3 font-display text-lg font-medium text-pb-ink">
            {idx + 1}. Over the last 2 weeks, how often have you been bothered by:
            <span className="mt-1 block text-pb-ink font-normal">{q.prompt}</span>
          </legend>
          <div className="grid gap-2 md:grid-cols-2">
            {OPTIONS.map((opt) => {
              const current = q.id === "q1" ? q1 : q2;
              const setter = q.id === "q1" ? setQ1 : setQ2;
              const selected = current === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors ${
                    selected
                      ? "border-pb-primary bg-pb-primary-50 text-pb-ink"
                      : "border-pb-border bg-pb-surface text-pb-ink-2 hover:border-pb-primary/50"
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={opt.value}
                    checked={selected}
                    onChange={() => {
                      setter(opt.value);
                      // Changing an answer after submit invalidates the result.
                      if (submitted) setSubmitted(false);
                    }}
                    className="h-4 w-4 accent-pb-primary"
                  />
                  <span className="flex-1">{opt.label}</span>
                  <span className="font-mono text-xs text-pb-ink-3">{opt.value}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!bothAnswered}
          className="ba-btn ba-btn--primary"
          style={{ opacity: bothAnswered ? 1 : 0.5 }}
        >
          Score screen
        </button>
        {submitted && (
          <button
            type="button"
            onClick={handleReset}
            className="ba-btn ba-btn--ghost ba-btn--sm"
          >
            New patient
          </button>
        )}
      </div>

      {submitted && (
        <div
          className="rounded-lg border bg-pb-surface p-6"
          style={{
            borderColor: positive ? "var(--color-pb-accent)" : "var(--color-pb-primary)",
            borderLeftWidth: 3,
          }}
        >
          <div className="flex items-center gap-4">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-md font-display text-2xl font-semibold"
              style={{
                background: positive ? "rgba(242,126,98,0.12)" : "rgba(31,138,138,0.12)",
                color: positive ? "var(--color-pb-accent)" : "var(--color-pb-primary)",
              }}
            >
              {total}
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-pb-ink-3">
                GAD-2 total · out of 6
              </p>
              <p className="mt-1 font-display text-lg font-medium text-pb-ink">
                {positive ? "Positive screen" : "Negative screen"}
              </p>
            </div>
          </div>
          <p className="mt-4 text-sm text-pb-ink-2">
            {positive
              ? "Score ≥3 is the validated cut-point for likely generalised anxiety. Consider a GAD-7 follow-up or clinical review. Management decisions are clinician judgement — this tool is a screener, not a treatment plan."
              : "Score <3 is within the negative range on GAD-2. Continue usual care; re-screen as clinically indicated."}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handleCopy}
              className="ba-btn ba-btn--ghost ba-btn--sm"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                  Copy clinician note
                </>
              )}
            </button>
            <p className="text-[11px] text-pb-ink-3">
              Pastes as:{" "}
              <code className="font-mono text-[10px]">{clinicianNote}</code>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
